import { inject, Injectable, signal } from '@angular/core';

import { PublicDataCacheService } from '@core/cache/public-data-cache.service';
import { AccountService, type AccountProfileVm } from '@core/profile/account.service';

import { ThemeConfigService, type SegmentBranding } from './theme-config.service';

/** Clave del ultimo branding aplicado, para repintar antes del primer frame. */
const BRANDING_STORAGE_KEY = 'luxury-segment-branding';

/**
 * La tienda se ve como el segmento vigente del socio.
 *
 * Vive aparte del `ThemeConfigService` porque son dos responsabilidades
 * distintas: aquel sabe pintar unos tokens, este sabe de dónde sale el
 * segmento y cuándo hay que volver a preguntarlo. El shell lo llama al montar;
 * las transiciones de membresía —cambio de plan hoy, baja y reafiliación
 * después— lo llaman al terminar, que es cuando el segmento acaba de cambiar.
 */
@Injectable({ providedIn: 'root' })
export class SegmentBrandingService {
  private readonly account = inject(AccountService);
  private readonly theme = inject(ThemeConfigService);
  private readonly cache = inject(PublicDataCacheService);

  private readonly cambios = signal(0);

  /**
   * Sube cada vez que el socio cambia de segmento.
   *
   * Descartar el caché no basta: solo afecta a la SIGUIENTE petición, y la
   * página ya pintada se queda con los precios del segmento anterior hasta que
   * el socio navegue. Las vistas que muestran precios de socio observan esto
   * para recargarse solas.
   *
   * Arranca en 0 y solo se mueve con un cambio real, así que una vista puede
   * ignorar el 0 y saber que todo lo demás es un cambio de verdad.
   */
  readonly version = this.cambios.asReadonly();

  private readonly segmentoActual = signal<string | null>(null);
  /**
   * Código del segmento con el que se leyó el perfil. Quien cachea precios lo
   * mete en su clave: si el segmento cambia del lado del servidor —una baja
   * que se hace efectiva, el backoffice— la clave cambia sola en la siguiente
   * lectura del perfil, sin depender de que el cambio haya pasado por aquí.
   */
  readonly segmento = this.segmentoActual.asReadonly();

  /**
   * Lee el segmento vigente, repinta la tienda y devuelve el perfil leído para
   * que quien lo pidió no tenga que volver a buscarlo.
   *
   * `traxCambioDeSegmento` descarta además lo que la tienda tenga cacheado con
   * los precios del segmento anterior: el descuento sale de `segments[0]`, así
   * que un catálogo servido hace un minuto muestra el precio que el socio ya
   * no tiene.
   */
  async refrescar(
    opciones: { trasCambioDeSegmento?: boolean } = {},
  ): Promise<AccountProfileVm | null> {
    let profile: AccountProfileVm;
    try {
      profile = await this.account.getProfile();
    } catch {
      // Quedarse con el tema del tenant es un fallback correcto: se ve la
      // marca de Luxury, no una pantalla rota.
      return null;
    }

    this.segmentoActual.set(profile.segment?.code ?? null);
    if (profile.segment) {
      this.theme.applySegmentBranding(profile.segment);
      this.recordar(profile.segment);
    } else {
      this.olvidar();
    }
    if (opciones.trasCambioDeSegmento) {
      this.cache.deletePrefix('products:');
      this.cache.deletePrefix('home:');
      this.cambios.update((n) => n + 1);
    }
    return profile;
  }

  /**
   * Repinta el segmento guardado de forma SINCRONA, antes del primer frame.
   *
   * `refrescar()` necesita una ida al BE, asi que hasta que responde la tienda
   * se pinta con el navy del tenant y luego salta al color del socio: ese es el
   * parpadeo. Con el ultimo branding conocido en storage el primer frame ya sale
   * del color correcto, y la respuesta del BE solo corrige si de verdad cambio.
   *
   * En la primerisima visita no hay nada guardado y no se pinta nada: es
   * preferible a bloquear el arranque de la app esperando al perfil.
   */
  restaurarDesdeCache(): void {
    const guardado = this.leer();
    if (guardado) this.theme.applySegmentBranding(guardado);
  }

  /** Al cerrar sesion ya no hay socio: se borra para no repintar al siguiente. */
  olvidar(): void {
    try {
      this.storage()?.removeItem(BRANDING_STORAGE_KEY);
    } catch {
      // Es una optimizacion: si el navegador bloquea storage, seguimos igual.
    }
  }

  private recordar(branding: SegmentBranding): void {
    try {
      this.storage()?.setItem(BRANDING_STORAGE_KEY, JSON.stringify(branding));
    } catch {
      // Idem: sin storage solo se pierde el arranque sin parpadeo.
    }
  }

  private leer(): SegmentBranding | null {
    try {
      const crudo = this.storage()?.getItem(BRANDING_STORAGE_KEY);
      if (!crudo) return null;
      // Todos los campos de SegmentBranding son opcionales, asi que cualquier
      // objeto vale como tal; `applySegmentBranding` ya valida el color.
      const parsed: unknown = JSON.parse(crudo);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  }

  private storage(): Storage | null {
    try {
      return typeof window === 'undefined' ? null : window.localStorage;
    } catch {
      return null;
    }
  }
}
